/* global Notification, document, window */
import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { createChatClient } from '../runtime/mobile/chatClient';
import { kv } from '../runtime/mobile/kv';

const ChatCtx = createContext({ channels: [], unread: {}, total: 0, markRead: () => {}, refresh: () => {}, notifPerm: 'unsupported', askNotifications: () => {} });
const toMs = (v) => (v ? new Date(v).getTime() || 0 : 0);
// Kept deliberately slow: the free Firestore tier caps daily reads and this
// poller runs on every open device. 45s is plenty for a shift-chat.
const POLL_MS = 45000;

// One background poller for the whole app: keeps the channel list fresh,
// counts unread messages per channel, and raises a browser notification when a
// new message lands while the app is not in the foreground.
export function ChatProvider({ children }) {
  const { user, profile } = useAuth();
  const client = useMemo(() => (user ? createChatClient(user) : null), [user]);
  const myName = profile?.fullName || null;

  const [channels, setChannels] = useState([]);
  const [unread, setUnread] = useState({}); // channelId -> count
  const readRef = useRef({}); // channelId -> { at: ms, id: lastReadMsgId }
  const firedRef = useRef({}); // channelId -> lastMessageAt(ms) we already notified for
  const primedRef = useRef(false);
  const [notifPerm, setNotifPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');

  const storeKey = user ? `chat:read:${user.uid}` : null;

  useEffect(() => {
    if (!storeKey) return;
    kv.get(storeKey).then((raw) => { if (raw) { try { readRef.current = JSON.parse(raw) || {}; } catch { /* ignore */ } } });
  }, [storeKey]);

  const persist = useCallback(() => { if (storeKey) kv.set(storeKey, JSON.stringify(readRef.current)).catch(() => {}); }, [storeKey]);

  const askNotifications = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    try { setNotifPerm(await Notification.requestPermission()); } catch { /* ignore */ }
  }, []);

  const fire = useCallback((channel, count) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && !document.hidden) return;
    try {
      const n = new Notification(channel.title || 'Новое сообщение', {
        body: `${channel.lastMessageAuthor ? `${channel.lastMessageAuthor}: ` : ''}${channel.lastMessagePreview || ''}`.slice(0, 140) + (count > 1 ? ` (+${count - 1})` : ''),
        tag: channel.id,
        icon: '/icon-192.png',
      });
      n.onclick = () => { try { window.focus(); } catch { /* ignore */ } n.close(); };
    } catch { /* ignore */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!client) return;
    let list;
    try { list = await client.listChannels(); } catch { return; }
    setChannels(list);

    const firstRun = !primedRef.current;
    const nextUnread = {};
    for (const c of list) {
      const lastAt = toMs(c.lastMessageAt);
      // First time we ever see a channel: treat whatever is there now as
      // already seen — a fresh device shouldn't show a pile of "unread".
      if (readRef.current[c.id] == null) {
        readRef.current[c.id] = { at: lastAt, id: null };
        firedRef.current[c.id] = lastAt;
        nextUnread[c.id] = 0;
        continue;
      }
      if (firstRun && firedRef.current[c.id] == null) firedRef.current[c.id] = lastAt;
      const read = readRef.current[c.id];
      const isMine = c.lastMessageAuthor && myName && c.lastMessageAuthor === myName;
      if (!lastAt || lastAt <= read.at || isMine) { nextUnread[c.id] = 0; continue; }
      // Was: fetch every message in the channel to get an exact unread count.
      // That is a full message-collection read per unread channel per poll —
      // far too expensive on the free tier. The channel doc already tells us
      // "there is something new"; the badge just shows a dot.
      nextUnread[c.id] = 1;
      // Notify only once per new message — not every poll until it's read.
      if (!firstRun && lastAt > (firedRef.current[c.id] || 0)) {
        fire(c, 1);
        firedRef.current[c.id] = lastAt;
      }
    }
    persist();
    setUnread(nextUnread);
    primedRef.current = true;
  }, [client, myName, fire, persist]);

  useEffect(() => {
    if (!client) { setChannels([]); setUnread({}); primedRef.current = false; return undefined; }
    primedRef.current = false;
    client.ensure().catch(() => {}).finally(refresh);
    // Skip the tick while the tab is hidden — a backgrounded device shouldn't
    // keep spending reads. We still refresh on the way back to foreground.
    const tick = () => { if (typeof document === 'undefined' || !document.hidden) refresh(); };
    const id = setInterval(tick, POLL_MS);
    const onVisible = () => { if (typeof document !== 'undefined' && !document.hidden) refresh(); };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    };
  }, [client, refresh]);

  // Mark a channel read up to its latest message.
  const markRead = useCallback((channelId, latestMsg) => {
    if (!channelId) return;
    const at = latestMsg ? toMs(latestMsg.createdAt) : Date.now();
    readRef.current[channelId] = { at: Math.max(at, readRef.current[channelId]?.at || 0), id: latestMsg?.id || readRef.current[channelId]?.id || null };
    persist();
    setUnread((u) => (u[channelId] ? { ...u, [channelId]: 0 } : u));
  }, [persist]);

  // Delete this chat from my list (per device). The docs and the other party
  // are untouched; it reappears if the other side writes again.
  const clearChannel = useCallback(async (channelId) => {
    if (!client || !channelId) return;
    await client.clearChannel(channelId);
    delete readRef.current[channelId];
    delete firedRef.current[channelId];
    persist();
    setChannels((cs) => cs.filter((c) => c.id !== channelId));
    setUnread((u) => { const n = { ...u }; delete n[channelId]; return n; });
    refresh();
  }, [client, persist, refresh]);

  const total = useMemo(() => Object.values(unread).reduce((a, b) => a + (b || 0), 0), [unread]);

  const value = useMemo(() => ({ channels, unread, total, markRead, clearChannel, refresh, notifPerm, askNotifications }), [channels, unread, total, markRead, clearChannel, refresh, notifPerm, askNotifications]);
  return <ChatCtx.Provider value={value}>{children}</ChatCtx.Provider>;
}

export const useChat = () => useContext(ChatCtx);
